import { get, isEmpty, isNil } from 'lodash';
import {
  EMPTY_VEHICULE_EXPERTISE,
  EMPTY_VICTIM_EXPERTISE,
  PROCEDURE_STATUSES,
  VEHICULE_EXPERTISE_FIELDS,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import {
  ExpertReportType,
  IExpertGlobalReportState,
  TvehiculeExpertise,
  TvictimExpertise,
  validationByExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import { mapObject } from 'helpers/object-helper';
import { Config } from 'containers/AppContainer/typings';
import {
  Expertise,
  JudgementADDDocumentType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { EXPERTISE_FIELD } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/constants';
import {
  EXPERT_TYPES,
  TYPE,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/constants';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import { EXPERT_TYPE } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/constants';
import { STATUS_REJECTED } from 'containers/LawyerFeeNote/constants';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { VictimType } from 'typings/globalTypes';

export const getEvaluationConfigFields = (
  victimExpertise: TvictimExpertise,
) => {
  const vicExp = mapObject(victimExpertise, (el: unknown) => {
    if (el === null) return '';

    return el;
  });

  return {
    [VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE]: String(
      get(
        vicExp,
        VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE,
        '',
      ),
    ),
    [VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE]: String(
      get(
        vicExp,
        VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE,
        '',
      ),
    ),
    [VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE]: String(
      get(vicExp, VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE, ''),
    ),
    [VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE]: String(
      get(vicExp, VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE, ''),
    ),
  };
};

export const formatParamValue = (item: Config) => {
  if (item.percentage) {
    return `${item.title}; ${item.percentage}%`;
  }
  return `${item.title}`;
};

export const filterExpertises = (
  expertises: Expertise[],
  expertiseTypes: string[],
): Expertise[] => {
  return expertises.filter(expertise =>
    expertiseTypes.includes(`${expertise[EXPERTISE_FIELD.EXPERTISE_ID]}`),
  );
};

export const formatSavedExpertReportToMatchState = (
  judgementAddDocuments: JudgementADDDocumentType[],
  savedExpertReport: ExpertReportType,
) => {
  return judgementAddDocuments.map(addDocument => ({
    ...addDocument,
    expertises: addDocument.expertises.map(expertise => ({
      ...expertise,
      victims: expertise.victims
        .map(vic => {
          const victimExpertise =
            (get(savedExpertReport, 'victimExpertises', []).find(
              vicExp =>
                String(vicExp.victimId) === String(vic.id) &&
                String(vicExp.expertiseJudgementAddDocumentId) ===
                  String(expertise.expertiseJudgementAddDocumentId) &&
                String(vicExp.expertiseId) === String(expertise.expertiseId),
            ) as TvictimExpertise) || {};
          return !isEmpty(victimExpertise)
            ? {
                ...vic,
                sharedResponbility: get(addDocument, 'responsibilityShare'),
                victimExpertise,
              }
            : ({} as any);
        })
        .filter(item => !isEmpty(item)),
      vehicules: expertise.vehicules
        .map(veh => {
          const vehiculeExpertise =
            (get(savedExpertReport, 'vehiculeExpertises', []).find(
              vehExp =>
                String(vehExp.vehiculeId) === String(veh.id) &&
                String(vehExp.expertiseJudgementAddDocumentId) ===
                  String(expertise.expertiseJudgementAddDocumentId) &&
                String(vehExp.expertiseId) === String(expertise.expertiseId),
            ) as TvehiculeExpertise) || {};
          return !isEmpty(vehiculeExpertise)
            ? {
                ...veh,
                vehiculeExpertise,
              }
            : ({} as any);
        })
        .filter(item => !isEmpty(item)),
    })),
  }));
};

export const extractExpertReport = (
  expertReportState: IExpertGlobalReportState[],
  predicateVictim: Function,
  predicateVehicule: Function,
) => {
  const expertReport: ExpertReportType = {
    victimExpertises: [],
    vehiculeExpertises: [],
  };
  expertReportState.forEach(report =>
    report.expertises.forEach(exp => {
      exp.victims
        .filter(v => predicateVictim(v))
        .map(
          // eslint-disable-next-line array-callback-return
          v => {
            expertReport.victimExpertises.push({
              ...v.victimExpertise,
              sharedResponsability: !isNil(get(v, 'sharedResponbility', null))
                ? get(v, 'sharedResponbility', null)
                : get(report, 'responsibilityShare'),
              isFirstInstance: false,
              judgementDate: report.judgementDate,
            });
          },
        );
      exp.vehicules
        .filter(v => predicateVehicule(v))
        .map(v => expertReport.vehiculeExpertises.push(v.vehiculeExpertise));
    }),
  );
  return expertReport;
};

export const filterHelper = {
  filterExpertises,
};
export const filterDuplicatedForms = (newJADDExpertises: any) => {
  return newJADDExpertises
    .map((jadd: any) => {
      return {
        ...jadd,
        expertises: jadd.expertises
          .map((expertise: any) => {
            return {
              ...expertise,
              victims: expertise.victims.filter((victim: VictimType) => {
                return !victim.isOld;
              }),
              vehicules: expertise.vehicules.filter(
                (vehicle: any) => !vehicle.isOld,
              ),
            };
          })
          .filter(
            (expertise: any) =>
              !isEmpty(expertise.victims) || !isEmpty(expertise.vehicules),
          ),
      };
    })
    .filter((jadd: any) => !isEmpty(jadd.expertises));
};
export const distinctExpertReport = (
  judgementAddDocuments: JudgementADDDocumentType[],
  savedExpertReport: ExpertReportType,
) => {
  const oldJADDExpertises = getOldExpertises(
    judgementAddDocuments,
    savedExpertReport,
  );
  const newJADDExpertises = getNewExpertises(
    judgementAddDocuments,
    savedExpertReport,
  );

  const recentJaddExpertises = filterDuplicatedForms(newJADDExpertises);

  return {
    newJADDExpertises: recentJaddExpertises,
    oldJADDExpertises,
  };
};
export const getOldExpertises = (
  judgementAddDocuments: JudgementADDDocumentType[] = [],
  savedExpertReport: ExpertReportType,
) => {
  return judgementAddDocuments
    .map((jadd: any) => {
      return {
        ...jadd,
        expertises: jadd.expertises
          .map((exp: any) => {
            const victimExpertise = get(exp, 'victims', []).filter(
              (victim: VictimType) => {
                return get(savedExpertReport, EXPERT_TYPES.VICTIMS, []).some(
                  (oldExpertise: any) => {
                    return (
                      oldExpertise?.expertiseJudgementAddDocumentId ===
                        exp?.expertiseJudgementAddDocumentId &&
                      victim?.id === oldExpertise?.victimId &&
                      oldExpertise?.expertiseId === exp?.expertiseId
                    );
                  },
                );
              },
            );
            const vehiculeExpertise = get(exp, 'vehicules', []).filter(
              (vehicle: any) => {
                return get(savedExpertReport, EXPERT_TYPES.VEHICULES, []).some(
                  (oldExpertise: any) => {
                    return (
                      oldExpertise.expertiseJudgementAddDocumentId ===
                        exp.expertiseJudgementAddDocumentId &&
                      vehicle?.id === oldExpertise?.vehiculeId &&
                      oldExpertise?.expertiseId === exp?.expertiseId
                    );
                  },
                );
              },
            );

            return {
              ...exp,
              victims: victimExpertise,
              vehicules: vehiculeExpertise,
            };
          })
          .filter(
            (item: any) =>
              !isEmpty(get(item, 'victims', [])) ||
              !isEmpty(get(item, 'vehicules', [])),
          ),
      };
    })
    .filter((jadd: any) => !isEmpty(jadd.expertises));
};
export const getNewExpertises = (
  judgementAddDocuments: JudgementADDDocumentType[] = [],
  savedExpertReport: ExpertReportType,
) => {
  return judgementAddDocuments
    .map((jadd: any) => {
      return {
        ...jadd,
        expertises: jadd.expertises
          .map((exp: any) => {
            const victimExpertise = get(exp, 'victims', []).filter(
              (victim: VictimType) => {
                return !get(savedExpertReport, EXPERT_TYPES.VICTIMS, []).some(
                  (oldExpertise: any) => {
                    return (
                      oldExpertise?.expertiseJudgementAddDocumentId ===
                        exp?.expertiseJudgementAddDocumentId &&
                      victim?.id === oldExpertise?.victimId &&
                      oldExpertise?.expertiseId === exp?.expertiseId
                    );
                  },
                );
              },
            );

            const vehiculeExpertise = get(exp, 'vehicules', []).filter(
              (vehicle: any) => {
                return !get(savedExpertReport, EXPERT_TYPES.VEHICULES, []).some(
                  (oldExpertise: any) => {
                    return (
                      oldExpertise.expertiseJudgementAddDocumentId ===
                        exp.expertiseJudgementAddDocumentId &&
                      vehicle?.id === oldExpertise?.vehiculeId &&
                      oldExpertise?.expertiseId === exp?.expertiseId
                    );
                  },
                );
              },
            );
            return {
              ...exp,
              victims: victimExpertise,
              vehicules: vehiculeExpertise,
            };
          })
          .filter(
            (exp: any) =>
              !isEmpty(get(exp, 'victims', [])) ||
              !isEmpty(get(exp, 'vehicules', [])),
          ),
      };
    })
    .filter((jadd: any) => !isEmpty(get(jadd, 'expertises', [])));
};

export const isExpertiseRejected = (currentExpertise: any) =>
  get(currentExpertise, 'status', '') === PROCEDURE_STATUSES.REJECTED;
export const manageDeactivationButton = (
  expertiseType: string,
  deactivateButtons: validationByExpertise[] = [],
  currentExpertise: any,
  setDeactivateButton?: Function,
) => {
  const newDeactivationButtons = [...deactivateButtons];
  const deactivateButton =
    deactivateButtons.find((item: any) => {
      return (
        item.expertiseId === get(currentExpertise, 'expertiseId', '') &&
        get(item, 'expertiseJudgementAddDocumentId') ===
          get(currentExpertise, 'expertiseJudgementAddDocumentId', '') &&
        (expertiseType === TYPE.VICTIM
          ? get(item, 'victimId') === get(currentExpertise, 'victimId')
          : get(item, 'vehicleId') === get(currentExpertise, 'vehiculeId'))
      );
    }) || ({} as any);
  if (!isEmpty(deactivateButton)) {
    const index = newDeactivationButtons.indexOf(deactivateButton);

    newDeactivationButtons[index].show = !get(
      newDeactivationButtons[index],
      'show',
      false,
    );
  }
  return setDeactivateButton && setDeactivateButton(newDeactivationButtons);
};

export const showValidateButton = (
  currentExpertise: any,
  judgementAddDocuments: JudgementADDDocumentType[],
  savedExpertReport: ExpertReportType,
  deactivateButtons: validationByExpertise[] = [],
  expertiseType: string,
  lawyerInfo: any,
  isCreator?: boolean,
  assignedLawyers?: string[],
  isManager?: boolean,
) => {
  const deactivateButton =
    deactivateButtons.find((item: any) => {
      return (
        item.expertiseId === get(currentExpertise, 'expertiseId', '') &&
        get(item, 'expertiseJudgementAddDocumentId') ===
          get(currentExpertise, 'expertiseJudgementAddDocumentId', '') &&
        (expertiseType === TYPE.VICTIM
          ? get(item, 'victimId') === get(currentExpertise, 'victimId', '')
          : get(item, 'vehicleId') === get(currentExpertise, 'vehiculeId', ''))
      );
    }) || {};

  const { newJADDExpertises = [] } = distinctExpertReport(
    judgementAddDocuments,
    savedExpertReport,
  );

  return isExpertiseRejected(currentExpertise)
    ? !isEmpty(get(currentExpertise, 'creator', ''))
      ? get(currentExpertise, 'lawyerCode', '') === get(lawyerInfo, 'sagisCode')
      : isCreator
    : !isEmpty(newJADDExpertises) &&
        get(deactivateButton, 'show', false) &&
        ((assignedLawyers &&
          assignedLawyers.includes(get(lawyerInfo, 'sagisCode'))) ||
          (isManager &&
            featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER)));
};
export const expertReportUtils = {
  distinctExpertReport,
  getOldExpertises,
  getNewExpertises,
};
export const formatExpertReport = (expertises: Expertise[]) => {
  return expertises.map(expertise => {
    const victimWithExpertises = get(expertise, 'victims', []).map(
      (v: VictimType) => ({
        ...v,
        victimExpertise: {
          ...EMPTY_VICTIM_EXPERTISE,
          [VICTIM_EXPERTISE_FIELDS.EXPERTISE_JUDGEMENT_ADD_DOCUMENT_ID]: `${get(
            expertise,
            'expertiseJudgementAddDocumentId',
            '',
          )}`,
          [VICTIM_EXPERTISE_FIELDS.VICTIM_ID]: `${v.id}`,
          [VICTIM_EXPERTISE_FIELDS.EXPERTISE_ID]: `${expertise.expertiseId}`,
          isNew: true,
        },
      }),
    );
    const vehiculeWithExpertises = get(expertise, 'vehicules', []).map(
      (veh: Vehicule) => ({
        ...veh,
        vehiculeExpertise: {
          ...EMPTY_VEHICULE_EXPERTISE,
          [VEHICULE_EXPERTISE_FIELDS.EXPERTISE_JUDGEMENT_ADD_DOCUMENT_ID]: `${get(
            expertise,
            'expertiseJudgementAddDocumentId',
            '',
          )}`,
          [VEHICULE_EXPERTISE_FIELDS.VEHICULE_ID]: `${veh.id}`,
          [VEHICULE_EXPERTISE_FIELDS.EXPERTISE_ID]: `${expertise.expertiseId}`,
          isNew: true,
        },
      }),
    );
    return {
      ...expertise,
      victims: victimWithExpertises,
      vehicules: vehiculeWithExpertises,
    };
  });
};
export const formatExpertReportState = (
  judgementAddDocuments: JudgementADDDocumentType[],
): IExpertGlobalReportState[] => {
  return judgementAddDocuments.map(addDocument => {
    const expertises = formatExpertReport(addDocument.expertises);
    return {
      ...addDocument,
      expertises,
    };
  });
};

export const getExpertise = (expertise: any) => {
  return !isNil(get(expertise, EXPERT_TYPE.VICTIMS))
    ? {
        EXPERT_FIELDS: VICTIM_EXPERTISE_FIELDS,
        EXPERT_TYPE: EXPERT_TYPE.VICTIMS,
        DOCUMENT: 'VICTIM_EXPERTISE_DOCUMENTS',
      }
    : {
        EXPERT_FIELDS: VEHICULE_EXPERTISE_FIELDS,
        EXPERT_TYPE: EXPERT_TYPE.VEHICULES,
        DOCUMENT: 'VEHICULE_EXPERTISE_DOCUMENTS',
      };
};
export const getDeactivationButton = (
  victimExpertises: any[],
  vehiculeExpertises: any[],
) => {
  const victimExp = victimExpertises
    .filter(item => !isNil(item.victimId))
    .map((item: any) => {
      return {
        victimId: get(item, 'victimId'),
        expertiseId: get(item, 'expertiseId'),
        expertiseJudgementAddDocumentId: get(
          item,
          'expertiseJudgementAddDocumentId',
        ),
        show:
          (isEmpty(item.status) || item.status === STATUS_REJECTED) &&
          !item.isOld
            ? true
            : false,
      };
    });
  const vehicleExp = vehiculeExpertises
    .filter(item => !isNil(item.vehiculeId))
    .map((item: any) => {
      return {
        vehicleId: get(item, 'vehiculeId'),
        expertiseId: get(item, 'expertiseId'),
        expertiseJudgementAddDocumentId: get(
          item,
          'expertiseJudgementAddDocumentId',
        ),

        show:
          (isEmpty(item.status) || item.status === STATUS_REJECTED) &&
          !item.isOld
            ? true
            : false,
      };
    });

  return [...victimExp, ...vehicleExp];
};
export const showUpdateButton = (
  expertReport: ExpertReportType,
  type: string,
  currentExpertise: any,
  isManager?: boolean,
) => {
  const currentExpertisesType =
    type === TYPE.VICTIM
      ? get(expertReport, 'victimExpertises', [])
      : (get(expertReport, 'vehiculeExpertises', []) as any);
  const currentExpertiseState =
    currentExpertisesType.find(
      (item: any) => get(currentExpertise, 'id', '') === get(item, 'id', ''),
    ) || {};

  return (
    !isEmpty(currentExpertiseState) &&
    isManager &&
    featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER)
  );
};
